import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListSubheader,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { MIN_QUERY_LENGTH, useCustomerSearch } from "../api/useEntitySearch";
import { DenseList, Row } from "../components/Rows";
import StateBadge from "../components/StateBadge";
import { Notice } from "../components/States";
import TopBar from "../components/TopBar";
import { useActiveOrg } from "../org/OrgContext";
import { useRecentCustomers } from "../recentCustomers";
import { useDebounced } from "../useDebounced";

export default function CustomerSelector() {
  const intl = useIntl();
  const org = useActiveOrg();
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 300);
  const search = useCustomerSearch(debounced);
  const { recent } = useRecentCustomers(org.segment);
  const searching = debounced.trim().length >= MIN_QUERY_LENGTH;

  return (
    <>
      <TopBar title={<FormattedMessage defaultMessage="Customers" />} />
      <Box sx={{ p: 1, bgcolor: "background.paper", position: "sticky", top: 48, zIndex: 1 }}>
        <TextField
          fullWidth
          size="small"
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={intl.formatMessage({
            defaultMessage: "Account, name or address",
          })}
          slotProps={{
            htmlInput: {
              autoCapitalize: "off",
              autoCorrect: "off",
              spellCheck: false,
              enterKeyHint: "search",
              inputMode: "search",
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() => setQuery("")}
                    aria-label={intl.formatMessage({ defaultMessage: "Clear" })}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Box>
      {searching && search.isFetching && <LinearProgress />}

      {searching ? (
        search.isError ? (
          <Notice>
            <FormattedMessage defaultMessage="The search failed. Check the connection and try again." />
          </Notice>
        ) : search.data && search.data.length === 0 ? (
          <Notice>
            <FormattedMessage defaultMessage="No customer found." />
          </Notice>
        ) : (
          <DenseList>
            {search.data?.map((c) => (
              <Row
                key={c.id}
                href={`/c/${c.id}`}
                id={c.account}
                primary={c.name}
                secondary={c.address}
                muted={c.active === false}
                end={<StateBadge active={c.active} tempInactiveTs={c["temp-inactive-ts"]} />}
              />
            ))}
          </DenseList>
        )
      ) : recent.length > 0 ? (
        <DenseList>
          <ListSubheader sx={{ lineHeight: "32px" }}>
            <FormattedMessage defaultMessage="Recently viewed" />
          </ListSubheader>
          {recent.map((c) => (
            <Row
              key={c.id}
              href={`/c/${c.id}`}
              id={c.account}
              primary={c.name}
              secondary={c.address}
            />
          ))}
        </DenseList>
      ) : (
        <Notice>
          <FormattedMessage
            defaultMessage="Type at least {count, plural, one {# character} other {# characters}} to search."
            values={{ count: MIN_QUERY_LENGTH }}
          />
        </Notice>
      )}
    </>
  );
}
